/**
 * Sound Manager - Simple audio system using Web Audio API
 * No external audio files needed - generates sounds programmatically
 */

class SoundManager {
  private audioContext: AudioContext | null = null;
  private enabled: boolean = true;
  private volume: number = 0.5;

  private getContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    // Resume context if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    return this.audioContext;
  }

  /**
   * Play a tone with specified frequency, duration, and type
   */
  private playTone(
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine',
    gainValue: number = this.volume
  ): void {
    if (!this.enabled) return;

    try {
      const ctx = this.getContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

      // Envelope for smoother sound
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(gainValue, ctx.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(gainValue * 0.7, ctx.currentTime + duration * 0.5);
      gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (error) {
      console.warn('Sound playback failed:', error);
    }
  }

  /**
   * Play a sequence of notes
   */
  private playSequence(
    notes: { freq: number; duration: number; delay: number }[],
    type: OscillatorType = 'sine'
  ): void {
    if (!this.enabled) return;

    notes.forEach((note) => {
      setTimeout(() => {
        this.playTone(note.freq, note.duration, type);
      }, note.delay * 1000);
    });
  }

  // ===================== GAME SOUNDS =====================

  /**
   * 🎮 Game Start / Round Start - Exciting ascending melody
   */
  playGameStart(): void {
    this.playSequence([
      { freq: 523, duration: 0.1, delay: 0 },      // C5
      { freq: 659, duration: 0.1, delay: 0.1 },    // E5
      { freq: 784, duration: 0.1, delay: 0.2 },    // G5
      { freq: 1047, duration: 0.3, delay: 0.3 },   // C6
    ], 'sine');
  }

  /**
   * 🗳️ Voting Start - Alert sound
   */
  playVotingStart(): void {
    this.playSequence([
      { freq: 880, duration: 0.15, delay: 0 },     // A5
      { freq: 880, duration: 0.15, delay: 0.2 },   // A5
      { freq: 1175, duration: 0.3, delay: 0.4 },   // D6
    ], 'triangle');
  }

  /**
   * 🏆 Victory / Win - Happy triumphant melody
   */
  playWin(): void {
    this.playSequence([
      { freq: 523, duration: 0.15, delay: 0 },     // C5
      { freq: 659, duration: 0.15, delay: 0.15 },  // E5
      { freq: 784, duration: 0.15, delay: 0.3 },   // G5
      { freq: 1047, duration: 0.15, delay: 0.45 }, // C6
      { freq: 784, duration: 0.15, delay: 0.6 },   // G5
      { freq: 1047, duration: 0.4, delay: 0.75 },  // C6
    ], 'sine');
  }

  /**
   * 💀 Loss / Defeat - Sad descending melody
   */
  playLose(): void {
    this.playSequence([
      { freq: 392, duration: 0.2, delay: 0 },      // G4
      { freq: 349, duration: 0.2, delay: 0.2 },    // F4
      { freq: 330, duration: 0.2, delay: 0.4 },    // E4
      { freq: 262, duration: 0.5, delay: 0.6 },    // C4
    ], 'sine');
  }

  /**
   * 💬 Message Received - Soft notification
   */
  playMessageReceived(): void {
    this.playSequence([
      { freq: 880, duration: 0.08, delay: 0 },     // A5
      { freq: 1318, duration: 0.12, delay: 0.08 }, // E6
    ], 'sine');
  }

  /**
   * 📤 Message Sent - Quick confirmation
   */
  playMessageSent(): void {
    this.playTone(1200, 0.08, 'sine', this.volume * 0.4);
  }

  /**
   * 🗳️ Vote Received - Someone voted
   */
  playVoteReceived(): void {
    this.playTone(660, 0.1, 'triangle', this.volume * 0.5);
  }

  /**
   * ⏰ Timer Warning - Urgent beeps
   */
  playTimerWarning(): void {
    this.playSequence([
      { freq: 800, duration: 0.1, delay: 0 },
      { freq: 800, duration: 0.1, delay: 0.2 },
    ], 'square');
  }

  /**
   * 👥 Player Joined - Welcoming sound
   */
  playPlayerJoined(): void {
    this.playSequence([
      { freq: 440, duration: 0.1, delay: 0 },      // A4
      { freq: 554, duration: 0.15, delay: 0.1 },   // C#5
    ], 'sine');
  }

  /**
   * 👋 Player Left - Exit sound
   */
  playPlayerLeft(): void {
    this.playSequence([
      { freq: 554, duration: 0.1, delay: 0 },      // C#5
      { freq: 440, duration: 0.15, delay: 0.1 },   // A4
    ], 'sine');
  }

  /**
   * ❌ Error - Warning sound
   */
  playError(): void {
    this.playSequence([
      { freq: 200, duration: 0.15, delay: 0 },
      { freq: 200, duration: 0.15, delay: 0.2 },
    ], 'sawtooth');
  }

  /**
   * ✅ Success - Quick positive feedback
   */
  playSuccess(): void {
    this.playSequence([
      { freq: 523, duration: 0.1, delay: 0 },      // C5
      { freq: 784, duration: 0.15, delay: 0.1 },   // G5
    ], 'sine');
  }

  /**
   * 🔔 Notification - General notification
   */
  playNotification(): void {
    this.playSequence([
      { freq: 587, duration: 0.1, delay: 0 },      // D5
      { freq: 784, duration: 0.15, delay: 0.1 },   // G5
    ], 'sine');
  }

  /**
   * 🎭 Role Reveal (Spy) - Mysterious sound
   */
  playSpyReveal(): void {
    this.playSequence([
      { freq: 220, duration: 0.2, delay: 0 },      // A3
      { freq: 277, duration: 0.2, delay: 0.2 },    // C#4
      { freq: 330, duration: 0.3, delay: 0.4 },    // E4
    ], 'triangle');
  }

  /**
   * 🎯 Role Reveal (Player) - Bright sound
   */
  playPlayerReveal(): void {
    this.playSequence([
      { freq: 523, duration: 0.15, delay: 0 },     // C5
      { freq: 659, duration: 0.15, delay: 0.15 },  // E5
      { freq: 784, duration: 0.2, delay: 0.3 },    // G5
    ], 'sine');
  }

  // ===================== SETTINGS =====================

  /**
   * Enable/disable all sounds
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    localStorage.setItem('soundEnabled', String(enabled));
  }

  /**
   * Check if sounds are enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Toggle sounds on/off
   */
  toggle(): boolean {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  /**
   * Set volume (0.0 to 1.0)
   */
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    localStorage.setItem('soundVolume', String(this.volume));
  }

  /**
   * Get current volume
   */
  getVolume(): number {
    return this.volume;
  }

  /**
   * Initialize from localStorage
   */
  init(): void {
    const savedEnabled = localStorage.getItem('soundEnabled');
    const savedVolume = localStorage.getItem('soundVolume');
    
    if (savedEnabled !== null) {
      this.enabled = savedEnabled === 'true';
    }
    if (savedVolume !== null) {
      this.volume = parseFloat(savedVolume);
    }
  }
}

// Singleton instance
export const soundManager = new SoundManager();

// Initialize on import
soundManager.init();

export default soundManager;
